/**
 * Pure helpers for the "Locations & Communities You Follow" page (GROWTH-7042).
 * Behaviour inventory: docs/testing/inventories/followingView.md
 */

// "Global" holds only "All Communities" (the switch covers it); language is a filter, not a group.
const EXCLUDED_GROUP_TITLES = ["global", "by language"];
const SUMMARY_NAME_LIMIT = 4;
const PLACE_GROUP_ORDER = ["zip", "city", "state", "country"];

const byTitle = (a, b) => a.title.localeCompare(b.title);

/**
 * Followable community groups from the `state.main.communities` tree.
 *
 * @param {Array} tree GET /communities?command=tree data
 * @returns {Array<{id: number, title: string, items: Array<{id: number, title: string}>}>}
 */
export const buildCommunityGroups = tree =>
  (Array.isArray(tree) ? tree : [])
    .filter(
      group =>
        group &&
        !EXCLUDED_GROUP_TITLES.includes(
          String(group.heritage_group_title || "")
            .trim()
            .toLowerCase()
        )
    )
    .map(group => {
      const seen = new Set();
      const items = (Array.isArray(group.children) ? group.children : [])
        .map(child => ({ id: Number(child.heritage_id), title: String(child.heritage_title || "") }))
        .filter(item => item.id > 0 && !seen.has(item.id) && seen.add(item.id))
        .sort(byTitle);

      return { id: Number(group.heritage_group_id), title: String(group.heritage_group_title), items };
    })
    .filter(group => group.items.length > 0);

/**
 * Unique community ids across groups (a community can sit in several groups).
 */
export const communityIds = groups => [...new Set((groups || []).flatMap(group => group.items.map(item => item.id)))];

/**
 * id → title across groups.
 */
export const communityTitles = groups => {
  const titles = new Map();
  (groups || []).forEach(group => group.items.forEach(item => titles.set(item.id, item.title)));

  return titles;
};

/**
 * Applies the community search and the language filter; `visible` holds what is shown.
 *
 * @param {Array} groups from buildCommunityGroups
 * @param {{search?: string, languageIds?: number[]|null}} filters languageIds null = any language
 */
export const filterGroups = (groups, { search = "", languageIds = null } = {}) => {
  const needle = String(search || "")
    .trim()
    .toLowerCase();
  const allowed = Array.isArray(languageIds) ? new Set(languageIds) : null;

  return (Array.isArray(groups) ? groups : [])
    .map(group => ({
      ...group,
      visible: group.items.filter(item => (!needle || item.title.toLowerCase().includes(needle)) && (!allowed || allowed.has(item.id)))
    }))
    .filter(group => group.visible.length > 0);
};

/**
 * Header counts and action for one group: counts cover the full group, the action the visible part.
 *
 * @returns {{selected: number, total: number, action: "select"|"deselect"}}
 */
export const groupStatus = (group, selected) => {
  const visible = group.visible || group.items;

  return {
    selected: group.items.filter(item => selected.has(item.id)).length,
    total: group.items.length,
    action: visible.length > 0 && visible.every(item => selected.has(item.id)) ? "deselect" : "select"
  };
};

/**
 * A new selection with `ids` switched on or off.
 */
export const setSelected = (selected, ids, on) => {
  const next = new Set(selected);
  (ids || []).forEach(id => (on ? next.add(id) : next.delete(id)));

  return next;
};

/**
 * @returns {{label: string, count: number, cityTotal: number}} label is "" when there are no locations
 */
export const summarizeLocations = locations => {
  const list = Array.isArray(locations) ? locations : [];

  return {
    label: list.map(location => location.name).join(", "),
    count: list.length,
    cityTotal: list.reduce((sum, location) => sum + (Number(location.city_count) || 0), 0)
  };
};

/**
 * @param {boolean} all
 * @param {Set<number>} selected
 * @param {Map<number, string>} titles
 * @returns {{kind: "all"|"some"|"none", count: number, names: string[], more: number}}
 */
export const summarizeCommunities = (all, selected, titles) => {
  if (all) {
    return { kind: "all", count: 0, names: [], more: 0 };
  }
  const names = [...(selected || [])]
    .map(id => titles?.get(id))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
  if (!names.length) {
    return { kind: "none", count: 0, names: [], more: 0 };
  }

  return {
    kind: "some",
    count: names.length,
    names: names.slice(0, SUMMARY_NAME_LIMIT),
    more: Math.max(0, names.length - SUMMARY_NAME_LIMIT)
  };
};

/**
 * Splits `text` around the first case-insensitive occurrence of `query`, for bolding.
 *
 * @returns {Array<{text: string, match: boolean}>}
 */
export const splitMatch = (text, query) => {
  const value = String(text || "");
  if (!value) {
    return [];
  }
  const needle = String(query || "").trim();
  const at = needle ? value.toLowerCase().indexOf(needle.toLowerCase()) : -1;
  if (at < 0) {
    return [{ text: value, match: false }];
  }

  return [
    { text: value.slice(0, at), match: false },
    { text: value.slice(at, at + needle.length), match: true },
    { text: value.slice(at + needle.length), match: false }
  ].filter(part => part.text !== "");
};

/**
 * Groups GET /places results for the dropdown: zip matches first, then city, state, country.
 *
 * @param {Array} results
 * @param {Set<string>} followedKeys location keys already followed
 * @returns {Array<{group: string, items: Array}>}
 */
export const groupPlaceResults = (results, followedKeys) => {
  const followed = followedKeys || new Set();
  const buckets = {};
  (Array.isArray(results) ? results : []).forEach(result => {
    const group = result.match === "zip" ? "zip" : result.level;
    if (!PLACE_GROUP_ORDER.includes(group)) {
      return;
    }
    buckets[group] = [...(buckets[group] || []), { ...result, following: followed.has(result.key) }];
  });

  return PLACE_GROUP_ORDER.filter(group => buckets[group]).map(group => ({ group, items: buckets[group] }));
};

/**
 * What a community choice needs before it can be autosaved (GROWTH-7102).
 *
 * @param {{all?: boolean, selectedCount?: number, locationCount?: number}} [choice]
 * @returns {"empty"|"held"|"save"} "empty": Follow all is off and nothing is picked — never sent;
 *   "held": no followed location to apply it to yet; "save": ready to send
 */
export const autosaveStep = ({ all = false, selectedCount = 0, locationCount = 0 } = {}) => {
  if (!all && !selectedCount) {
    return "empty";
  }

  return locationCount ? "save" : "held";
};

/**
 * PUT /user-following/communities body for a community choice; ids sorted so equal choices compare equal.
 *
 * @param {{all: boolean, selected: Set<number>}} [choice]
 * @returns {{all: boolean, heritage_ids: number[]}}
 */
export const communityPayload = choice => {
  const all = Boolean(choice?.all);

  return { all, heritage_ids: all ? [] : [...(choice?.selected || [])].map(Number).sort((a, b) => a - b) };
};

/**
 * Whether two community choices save the same thing: order does not matter, and the picked ids do
 * not count while "all" is on.
 */
export const sameCommunities = (a, b) => {
  const left = communityPayload(a);
  const right = communityPayload(b);

  return (
    left.all === right.all &&
    left.heritage_ids.length === right.heritage_ids.length &&
    left.heritage_ids.every((id, index) => id === right.heritage_ids[index])
  );
};
